import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Instagram, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, tx } from "./i18n";
import { Reveal, Eyebrow, useSeo } from "./bits";
import { CaseVisual, KpiStrip } from "./CaseCard";
import { InstagramEmbeds } from "./InstagramEmbeds";

const Block = ({ title, text, n }) => (
  <Reveal delay={n * 90} className="rounded-[24px] bg-white p-6 sm:p-8 border border-[#7CC6FA]/50" data-testid={`case-block-${n}`}>
    <div className="flex items-center gap-3"><span className="h-8 w-8 rounded-full bg-[#E8173A] text-white text-sm font-black flex items-center justify-center">{n + 1}</span><h2 className="font-display text-xl font-black">{title}</h2></div>
    <p className="mt-3 text-slate-700 leading-relaxed whitespace-pre-line">{text}</p>
  </Reveal>
);

export default function CaseDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [c, setC] = useState(undefined);
  useEffect(() => { api.get(`/public/case-history/${slug}`).then((r) => setC(r.data)).catch(() => setC(null)); }, [slug]);
  useSeo(c ? `${c.nome} – Case history Core` : t.seo.title, c ? tx(c.sintesi, lang) : t.seo.desc);
  if (c === undefined) return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!c) return <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4 px-5 text-center" data-testid="case-notfound"><p className="text-lg font-bold">{t.casi.notfound}</p><Link to="/#casi" className="font-bold text-[#E8173A]">{t.casi.back}</Link></div>;
  return (
    <div className="pt-[calc(72px+env(safe-area-inset-top))]" data-testid="case-detail">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-8 pb-20">
        <Link to="/#casi" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0A1128]/70 hover:text-[#E8173A]" data-testid="case-back"><ArrowLeft className="h-4 w-4" />{t.casi.back}</Link>
        <Reveal className="mt-6"><CaseVisual c={c} className="h-64 sm:h-80 rounded-[32px]" /></Reveal>
        <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
          <div><Eyebrow>{tx(c.settore, lang)}</Eyebrow><h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight" data-testid="case-title">{c.nome}</h1><p className="mt-3 text-lg text-slate-600 max-w-2xl">{tx(c.sintesi, lang)}</p></div>
          {c.instagram && <a href={c.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-[#0A1128] text-white font-bold hover:bg-[#E8173A] transition-colors" data-testid="case-detail-ig"><Instagram className="h-5 w-5" />Instagram</a>}
        </div>
        <div className="mt-8"><div className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A1128]/60 mb-3">{t.casi.kpi}</div><KpiStrip c={c} big /></div>
        <div className="mt-10 grid gap-4">
          <Block n={0} title={t.casi.sfida} text={tx(c.sfida, lang)} />
          <Block n={1} title={t.casi.soluzione} text={tx(c.soluzione, lang)} />
          <Block n={2} title={t.casi.risultati} text={tx(c.risultati, lang)} />
        </div>
        {!!c.servizi?.length && (
          <div className="mt-10"><div className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A1128]/60 mb-3">{t.casi.servizi}</div>
            <div className="flex flex-wrap gap-2" data-testid="case-services">{c.servizi.map((k) => t.servizi.items[k] && <span key={k} className="rounded-full bg-[#7CC6FA]/40 px-4 py-2 text-sm font-bold">{t.servizi.items[k][0]}</span>)}</div></div>
        )}
        {!!c.gallery_urls?.length && (
          <div className="mt-10 grid gap-3 grid-cols-2 sm:grid-cols-3" data-testid="case-gallery">
            {c.gallery_urls.map((u) => <img key={u} src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" loading="lazy" className="aspect-square w-full rounded-2xl object-cover" />)}
          </div>
        )}
        {!!c.instagram_posts?.length && <InstagramEmbeds urls={c.instagram_posts} />}
        <div className="mt-14 rounded-[28px] bg-[#0A1128] text-white p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div className="font-display text-2xl font-black">{t.contatti.title}</div>
          <Link to="/#contatti" className="h-12 px-6 rounded-full bg-[#E8173A] font-bold inline-flex items-center" data-testid="case-cta-contact">{t.hero.cta2}</Link>
        </div>
      </div>
    </div>
  );
}
